import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Checkbox, Modal } from '@/components/ui/daisy';
import Lesson from '@/models/Lesson';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';

export type PublishLessonFormFields = {
  lessonIds: string[];
};

export type PublishLessonFormProps = {
  lessons: Lesson[];
  isLoading?: boolean;
  onSubmit: (data: PublishLessonFormFields) => void;
  onCancel: () => void;
};

export default function PublishLessonForm({
  lessons,
  isLoading,
  onSubmit,
  onCancel,
}: PublishLessonFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.pedContentTab',
  });
  const { t: tModal } = useTranslation('translation', {
    keyPrefix: 'confirmModal',
  });

  const mapLessonsIds = lessons.map(lesson => String(lesson.id));

  const { register, handleSubmit, watch, setValue } =
    useForm<PublishLessonFormFields>({
      values: { lessonIds: mapLessonsIds },
    });

  const selectedIds = watch('lessonIds');
  const hasSelection = selectedIds.length > 0;
  const isAllSelected = selectedIds.length === lessons.length;

  const toggleAll = () =>
    setValue('lessonIds', isAllSelected ? [] : mapLessonsIds);

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
      <ul className="flex flex-col gap-3 max-h-[40vh] overflow-y-auto">
        {lessons.map(lesson => (
          <li key={lesson.id}>
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                color="primary"
                value={lesson.id}
                {...register('lessonIds')}
              />
              {lesson.name}
            </label>
          </li>
        ))}
      </ul>
      <div>
        <ConditionalRenderer condition={lessons.length > 1}>
          <label className="flex items-center font-semibold gap-3 cursor-pointer mt-4">
            <Checkbox checked={isAllSelected} onChange={toggleAll} />
            {t('selectAll')}
          </label>
        </ConditionalRenderer>
        <Modal.Action className="flex">
          <Button
            type="button"
            className="grow"
            variant="soft"
            onClick={onCancel}
            disabled={isLoading}
          >
            {tModal('cancel')}
          </Button>
          <Button
            type="submit"
            className="grow"
            color="primary"
            loading={isLoading}
            disabled={!hasSelection || isLoading}
          >
            {t('publish')}
          </Button>
        </Modal.Action>
      </div>
    </form>
  );
}
